import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Menu as MenuIcon } from '@mui/icons-material';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoginPage } from './pages/LoginPage';
import { hasPermission } from './lib/auth';
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage })));
import type { Permission } from './lib/types';
import { getErrorMessage, useToast } from './lib/toast';
import { ReloadPrompt } from './components/ReloadPrompt';
import { DevBanner } from './components/DevBanner';
import { PasskeyRegisterPrompt } from './components/PasskeyRegisterPrompt';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePatientAnalysisEvents } from './lib/usePatientAnalysisEvents';
import { AppDrawer } from './components/AppDrawer';

const drawerWidth = 260;
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const UnifiedPatientsPage = lazy(() => import('./pages/UnifiedPatientsPage').then((m) => ({ default: m.UnifiedPatientsPage })));
const PatientDetailPage = lazy(() => import('./pages/PatientDetailPage').then((m) => ({ default: m.PatientDetailPage })));
const UploadsPage = lazy(() => import('./pages/UploadsPage').then((m) => ({ default: m.UploadsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SurgicalListsCalendarPage = lazy(() => import('./pages/SurgicalListsCalendarPage').then((m) => ({ default: m.SurgicalListsCalendarPage })));
const MonitorPage = lazy(() => import('./pages/MonitorPage').then((m) => ({ default: m.MonitorPage })));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage').then((m) => ({ default: m.ChangelogPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const DocumentTypesPage = lazy(() => import('./pages/DocumentTypesPage').then((m) => ({ default: m.DocumentTypesPage })));
const PanoramicCalendarPage = lazy(() => import('./pages/PanoramicCalendarPage').then((m) => ({ default: m.PanoramicCalendarPage })));
const ExamListPage = lazy(() => import('./pages/ExamListPage'));
const ExamFormPage = lazy(() => import('./pages/ExamFormPage'));
const PracticeResultsPage = lazy(() =>
  import('./pages/practice/PracticeResultsPage').then((m) => ({ default: m.PracticeResultsPage }))
);
const CockpitPage = lazy(() =>
  import('./pages/practice/cockpit/CockpitPage').then((m) => ({ default: m.CockpitPage }))
);
const PracticeDemoPage = lazy(() =>
  import('./pages/practice/PracticeDemoPage').then((m) => ({ default: m.PracticeDemoPage }))
);
const AnalyticsPage = lazy(() =>
  import('./pages/practice/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage }))
);
const PracticeHistoryPage = lazy(() =>
  import('./pages/practice/PracticeHistoryPage').then((m) => ({ default: m.PracticeHistoryPage }))
);
const PreceptorDashboardPage = lazy(() =>
  import('./pages/preceptor/PreceptorDashboardPage').then((m) => ({ default: m.PreceptorDashboardPage }))
);
const NoticesPage = lazy(() => import('./pages/NoticesPage').then((m) => ({ default: m.NoticesPage })));
const TermsAcceptancePage = lazy(() => import('./pages/TermsAcceptancePage').then((m) => ({ default: m.TermsAcceptancePage })));
const InvitePage = lazy(() => import('./pages/InvitePage').then((m) => ({ default: m.InvitePage })));
const SetupPasskeyPage = lazy(() => import('./pages/SetupPasskeyPage').then((m) => ({ default: m.SetupPasskeyPage })));
const RecoveryStartPage = lazy(() => import('./pages/RecoveryStartPage').then((m) => ({ default: m.RecoveryStartPage })));
const RecoveryCompletePage = lazy(() => import('./pages/RecoveryCompletePage').then((m) => ({ default: m.RecoveryCompletePage })));
const FirstAccessPage = lazy(() => import('./pages/FirstAccessPage').then((m) => ({ default: m.FirstAccessPage })));
const StepUpModal = lazy(() => import('./components/StepUpModal').then((m) => ({ default: m.StepUpModal })));

// Rota legada: /practice/play/:sessionId foi substituída por /practice/cockpit/:sessionId
// no refactor 1. Mantemos este redirect porque PracticeResultsPage.handleRepeat ainda
// aponta para a rota antiga e o arquivo é produto real (refactor 2).
function LegacyPracticePlayRedirect() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <Navigate to={sessionId ? `/practice/cockpit/${sessionId}` : '/practice/scenarios'} replace />;
}

function ProtectedRoute({ permission, children }: { permission: Permission | Permission[]; children: React.ReactElement }) {
  const allowed = Array.isArray(permission)
    ? permission.some((p) => hasPermission(p))
    : hasPermission(permission);
  if (!allowed) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppShell() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user, logout, availableOrgs, switchOrg, preAuthToken } = useAuth();
  const [showPasskeyManual, setShowPasskeyManual] = useState(false);
  const { notifyError } = useToast();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  usePatientAnalysisEvents();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [switchingOrg, setSwitchingOrg] = useState(false);

  async function handleSwitchOrg(orgId: number): Promise<void> {
    if (!preAuthToken || switchingOrg) return;
    setSwitchingOrg(true);
    try {
      await switchOrg(preAuthToken, orgId);
    } catch (err) {
      notifyError(getErrorMessage(err, t('login.orgSelectError')));
    } finally {
      setSwitchingOrg(false);
    }
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {(!isDesktop || !desktopOpen) && (
        <IconButton
          color="inherit"
          onClick={() => (isDesktop ? setDesktopOpen(true) : setMobileOpen((prev) => !prev))}
          aria-label={t('common.menu')}
          size="small"
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: (z) => z.zIndex.drawer + 1,
            bgcolor: 'rgba(12, 17, 24, 0.85)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(148, 163, 184, 0.12)',
            '&:hover': { bgcolor: 'rgba(12, 17, 24, 0.95)' },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Drawer
        variant={isDesktop ? 'persistent' : 'temporary'}
        open={isDesktop ? desktopOpen : mobileOpen}
        onClose={() => (isDesktop ? setDesktopOpen(false) : setMobileOpen(false))}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: desktopOpen || !isDesktop ? drawerWidth : 0,
          flexShrink: 0,
          transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1)',
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
        }}
      >
        <AppDrawer
          user={user}
          availableOrgs={availableOrgs}
          preAuthToken={preAuthToken}
          switchingOrg={switchingOrg}
          onSwitchOrg={(orgId) => void handleSwitchOrg(orgId)}
          onLogout={logout}
          onManagePasskey={() => setShowPasskeyManual(true)}
          onCollapse={isDesktop ? () => setDesktopOpen(false) : undefined}
        />
      </Drawer>

      <PasskeyRegisterPrompt
        open={showPasskeyManual}
        onClose={() => setShowPasskeyManual(false)}
        onRegistered={() => setShowPasskeyManual(false)}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: { xs: 2, md: 4 },
          pr: { xs: 2, md: 4 },
          // Hamburger flutuante (top:12, left:12, ~44px) sobreporia o conteúdo
          // sem este left-padding extra quando o drawer está fechado.
          pl: !isDesktop || !desktopOpen ? 8 : { xs: 2, md: 4 },
          pt: !isDesktop || !desktopOpen ? 8 : 4,
          // height fixo (era minHeight): forca <main> a ser o scroll container real
          // do app, em vez do scroll vazar pro <html>. Sem isso, sticky bar adere
          // ao bottom do <main> (que cresce ate caber tudo, ~5625px) em vez do
          // viewport. Lucas observou v7.66.27 sticky em y=5436 num viewport 879.
          height: '100vh',
          minWidth: 0,
          overflow: 'auto',
        }}
      >
        <Stack spacing={3} className="fade-up">
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/patients" element={<UnifiedPatientsPage />} />
              <Route path="/patients/:id" element={<PatientDetailPage />} />
              <Route path="/uploads" element={<UploadsPage />} />
              <Route path="/surgical-list" element={<Navigate to="/patients" replace />} />
              <Route path="/surgical-lists" element={<SurgicalListsCalendarPage />} />
              {user?.isSuperAdmin && (
                <>
                  <Route path="/exam-sessions" element={<ProtectedRoute permission="exams.read"><ExamListPage /></ProtectedRoute>} />
                  <Route path="/exam-sessions/new" element={<ProtectedRoute permission="exams.read"><ExamFormPage /></ProtectedRoute>} />
                  <Route path="/exam-sessions/:id" element={<ProtectedRoute permission="exams.read"><ExamFormPage /></ProtectedRoute>} />
                </>
              )}
              <Route path="/settings" element={<ProtectedRoute permission={['settings.read', 'settings.write', 'users.manage', 'pii.config']}><SettingsPage /></ProtectedRoute>} />
              {/* "Sessões ativas" + "Minha conta" são per-user (no settings perm required).
                  Must come BEFORE /settings/:section so the more specific path matches first. */}
              <Route path="/settings/sessions" element={<SettingsPage />} />
              <Route path="/settings/account" element={<SettingsPage />} />
              <Route path="/settings/:section" element={<ProtectedRoute permission={['settings.read', 'settings.write', 'users.manage', 'pii.config']}><SettingsPage /></ProtectedRoute>} />
              <Route path="/document-types" element={<DocumentTypesPage />} />
              <Route path="/panoramic-calendar" element={<ProtectedRoute permission="rotation.read"><PanoramicCalendarPage /></ProtectedRoute>} />
              <Route path="/monitor" element={<ProtectedRoute permission="users.manage"><MonitorPage /></ProtectedRoute>} />
              <Route path="/changelog" element={user?.isSuperAdmin ? <ChangelogPage /> : <Navigate to="/" replace />} />
              <Route path="/practice" element={<Navigate to="/practice/scenarios" replace />} />
              <Route path="/practice/scenarios" element={
                <ProtectedRoute permission="practice.read">
                  <PracticeDemoPage />
                </ProtectedRoute>
              } />
              <Route path="/practice/analytics" element={
                <ProtectedRoute permission="practice.read">
                  <AnalyticsPage />
                </ProtectedRoute>
              } />
              <Route path="/practice/results/:sessionId" element={
                <ProtectedRoute permission="practice.read">
                  <PracticeResultsPage />
                </ProtectedRoute>
              } />
              <Route path="/practice/history" element={
                <ProtectedRoute permission="practice.read">
                  <PracticeHistoryPage />
                </ProtectedRoute>
              } />
              <Route path="/preceptor/sessions" element={
                <ProtectedRoute permission="practice.read-others">
                  <PreceptorDashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/notices" element={<NoticesPage />} />
              <Route path="/setup-passkey" element={<SetupPasskeyPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Stack>
      </Box>
    </Box>
  );
}

/** Sprint 3.4 (FE 7.51.0): global step-up modal listener.
 *  api.ts dispatches `dominoris.step-up-required` when backend returns 401
 *  STEP_UP_REQUIRED for an AAL3 endpoint. We mount the StepUpModal here once
 *  and let api.ts auto-retry the original request after `step-up-verified`. */
function StepUpModalGlobal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('dominoris.step-up-required', handler);
    return () => window.removeEventListener('dominoris.step-up-required', handler);
  }, []);

  const handleVerified = () => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent('dominoris.step-up-verified'));
  };
  const handleCancelled = () => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent('dominoris.step-up-cancelled'));
  };

  return <StepUpModal open={open} onVerified={handleVerified} onCancelled={handleCancelled} />;
}

function PageFallback() {
  return (
    <Box sx={{ px: 1 }}>
      <LinearProgress />
    </Box>
  );
}

function LoginRouter({ onSuccess }: { onSuccess: () => void }) {
  const [showAdmin, setShowAdmin] = useState(false);

  if (showAdmin) {
    return (
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>}>
        <AdminLoginPage onSuccess={onSuccess} onBack={() => setShowAdmin(false)} />
      </Suspense>
    );
  }

  return <LoginPage onSuccess={onSuccess} onAdminLogin={() => setShowAdmin(true)} />;
}

function getPasskeyDismissedKey(userId: string): string {
  return `dominoris.passkeyPromptDismissed.${userId}`;
}

function AppContent() {
  const { isAuthenticated, initializing, termsAccepted, user } = useAuth();
  const termsGateMode = import.meta.env.VITE_TERMS_GATE_MODE ?? 'hard';
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const prevAuthenticated = useRef(false);

  useEffect(() => {
    const justLoggedIn = !prevAuthenticated.current && isAuthenticated;
    prevAuthenticated.current = isAuthenticated;

    if (justLoggedIn && user?.id) {
      const dismissed = localStorage.getItem(getPasskeyDismissedKey(user.id));
      if (!dismissed) {
        setShowPasskeyPrompt(true);
      }
    }

    if (!isAuthenticated) {
      setShowPasskeyPrompt(false);
    }
  }, [isAuthenticated, user?.id]);

  function handlePasskeyPromptDismiss(): void {
    if (user?.id) localStorage.setItem(getPasskeyDismissedKey(user.id), '1');
    setShowPasskeyPrompt(false);
  }

  function handlePasskeyRegistered(): void {
    if (user?.id) localStorage.setItem(getPasskeyDismissedKey(user.id), '1');
    setShowPasskeyPrompt(false);
  }

  if (initializing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // EXK-157 fixup (2026-05-15): rotas públicas magic-link/invite precisam ser
  // alcançáveis INDEPENDENTE do status de auth. Antes dessas rotas estavam
  // dentro do bloco `if (!isAuthenticated)`, então admin que recebia link de
  // primeiro acesso (ou regenerava invite e clicava por engano) com sessão
  // ativa caía em 404 — o AppRouter autenticado não conhece esses paths.
  // Token no URL/query já é a prova de autenticação dessas páginas.
  const publicPath = window.location.pathname;
  const inviteMatch = publicPath.match(/^\/invite\/([a-f0-9-]+)$/i);
  if (inviteMatch && inviteMatch[1]) {
    return (
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>}>
        <InvitePage token={inviteMatch[1]} onSuccess={() => { /* Auth state update handled by AuthContext */ }} />
        <ReloadPrompt />
      </Suspense>
    );
  }
  // BrowserRouter wrap NECESSÁRIO porque essas páginas usam useNavigate() pra
  // redirect pós-success. Sem o Router context, useNavigate lança "may be used
  // only in the context of a <Router>" (bug FirstAccessPage 2026-05-10).
  if (publicPath === '/recovery/start') {
    return (
      <BrowserRouter>
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>}>
          <RecoveryStartPage />
          <ReloadPrompt />
        </Suspense>
      </BrowserRouter>
    );
  }
  if (publicPath === '/recovery/complete') {
    return (
      <BrowserRouter>
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>}>
          <RecoveryCompletePage />
          <ReloadPrompt />
        </Suspense>
      </BrowserRouter>
    );
  }
  if (publicPath === '/first-access') {
    return (
      <BrowserRouter>
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>}>
          <FirstAccessPage />
          <ReloadPrompt />
        </Suspense>
      </BrowserRouter>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <LoginRouter onSuccess={() => { /* Auth state update handled by AuthContext */ }} />
        <ReloadPrompt />
      </>
    );
  }

  if (termsGateMode === 'hard' && !termsAccepted) {
    return (
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>}>
        <TermsAcceptancePage />
        <ReloadPrompt />
      </Suspense>
    );
  }

  return (
    <BrowserRouter>
      <PasskeyRegisterPrompt
        open={showPasskeyPrompt}
        onClose={handlePasskeyPromptDismiss}
        onRegistered={handlePasskeyRegistered}
      />
      <Suspense fallback={null}>
        <StepUpModalGlobal />
      </Suspense>
      <Routes>
        {/* Cockpit: full-screen layout, sem AppShell */}
        <Route
          path="/practice/cockpit/:sessionId"
          element={
            <ProtectedRoute permission="practice.play">
              <Suspense fallback={<PageFallback />}>
                <CockpitPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* /practice/play/:sessionId — rota aposentada. Redirect preserva handleRepeat em PracticeResultsPage. */}
        <Route path="/practice/play/:sessionId" element={<LegacyPracticePlayRedirect />} />
        {/* /practice/demo — rota aposentada (refactor 1, 2026-04-18). Redirect para /practice/scenarios. */}
        <Route path="/practice/demo" element={<Navigate to="/practice/scenarios" replace />} />
        {/* Todas as outras rotas: dentro do AppShell */}
        <Route path="/*" element={<AppShell />} />
      </Routes>
      <ReloadPrompt />
    </BrowserRouter>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
        <DevBanner />
      </AuthProvider>
    </ErrorBoundary>
  );
}
